import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminApi, type AdminOrg } from "@/api/adminApi";
import {
  PoliciesApi,
  isRuleDefinition,
  type EffectivePolicyRow,
  type Industry,
  type PolicyDefinition,
  type PolicyKind,
  type PolicyPhase,
  type PolicyRow,
  type PolicyScope,
  type PolicyTier,
  type PreviewResult,
} from "@/api/policiesApi";
import { RestClient } from "@/api/restClient";

const apiUrl = import.meta.env.VITE_API_URL || "";
const client = new RestClient({ apiUrl });
const policiesApi = new PoliciesApi(client);
const adminApi = new AdminApi(client);


const TIERS: PolicyTier[] = ["platform", "industry", "company", "department", "user"];

const TIER_LABEL: Record<PolicyTier, string> = {
  platform: "Platform",
  industry: "Industry",
  company: "Company",
  department: "Department",
  user: "User",
};

/** Tiers whose rows are addressed by an id the admin must supply. */
const NEEDS_SCOPE_ID: PolicyTier[] = ["industry", "department", "user"];

const td = { padding: "8px 4px" } as const;
const th = { textAlign: "left" as const, padding: "8px 4px" };
const inputStyle = {
  padding: "6px 10px",
  border: "1px solid var(--border-color, #e0e0e0)",
  borderRadius: 6,
  fontSize: 13,
} as const;

function actionOf(def: PolicyDefinition | null | undefined): string {
  if (!def) return "—";
  return def.action;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// ---------------------------------------------------------------------------
// The kind selector. Its copy is load-bearing, not decoration: an author who
// writes a judge expecting PREVENTION has misunderstood the feature, and this
// is the moment to stop that.
// ---------------------------------------------------------------------------

function KindSelector({
  value,
  onChange,
}: {
  value: PolicyKind;
  onChange: (k: PolicyKind) => void;
}) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 6 }}>
        Kind
      </label>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        {(["rule", "judge"] as PolicyKind[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => onChange(k)}
            style={{
              padding: "4px 12px",
              fontSize: 13,
              cursor: "pointer",
              borderRadius: 4,
              border: value === k ? "1px solid var(--primary, #2563eb)" : "1px solid var(--border-color, #e0e0e0)",
              background: value === k ? "var(--primary-bg, #eff6ff)" : "transparent",
              fontWeight: value === k ? 600 : 400,
            }}
          >
            {k === "rule" ? "Rule" : "Judge"}
          </button>
        ))}
      </div>
      <div
        style={{
          fontSize: 12,
          lineHeight: 1.5,
          padding: 10,
          borderRadius: 6,
          background: "var(--surface-bg, #f9f9f9)",
          color: "var(--text-secondary, #555)",
        }}
      >
        <div style={{ marginBottom: 6 }}>
          <strong>Rule</strong> — deterministic patterns. Runs on both boundaries and can
          prevent output.
        </div>
        <div style={{ marginBottom: 6 }}>
          <strong>Judge</strong> — an LLM reads the text against your instruction. It
          advises. On a reply it runs after the text has streamed, so it flags and records —
          it cannot un-send anything. It also reads text an attacker may control.
        </div>
        <div style={{ fontWeight: 600, color: "var(--text-primary, #333)" }}>
          If it must not be bypassable, write a rule.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The key combobox. Pick-from-existing by default: platform `pii.ssn` and
// company `pii.ssn` BOTH applying is the union working, but an author typing
// `pii_ssn` believing they are tightening the platform rule creates an
// unrelated policy that governs nothing. A new key needs an explicit choice.
// ---------------------------------------------------------------------------

function KeyCombobox({
  value,
  onChange,
  knownKeys,
}: {
  value: string;
  onChange: (k: string) => void;
  knownKeys: string[];
}) {
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <label
        htmlFor="policy-key"
        style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 6 }}
      >
        Key
      </label>
      {creating ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            id="policy-key"
            type="text"
            aria-label="New policy key"
            placeholder="e.g. pii.ssn"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="button"
            onClick={() => {
              setCreating(false);
              onChange("");
            }}
            style={{ fontSize: 12, padding: "4px 10px", cursor: "pointer" }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            id="policy-key"
            aria-label="Key"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          >
            <option value="">Select an existing key…</option>
            {knownKeys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              setCreating(true);
              onChange("");
            }}
            style={{ fontSize: 12, padding: "4px 10px", cursor: "pointer" }}
          >
            Create new key
          </button>
        </div>
      )}
      <div style={{ fontSize: 11, color: "var(--text-secondary, #666)", marginTop: 6 }}>
        Reuse an existing key to tighten the same concern at this tier. A new key is a
        separate policy — it does not narrow or replace anything.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Author / edit form
// ---------------------------------------------------------------------------

function PolicyEditor({
  scope,
  knownKeys,
  editing,
  onSaved,
  onCancel,
}: {
  scope: PolicyScope;
  knownKeys: string[];
  editing: PolicyRow | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [key, setKey] = useState(editing?.key ?? "");
  const [phase, setPhase] = useState<PolicyPhase>(editing?.phase ?? "both");
  const [kind, setKind] = useState<PolicyKind>(editing?.kind ?? "rule");
  const [patterns, setPatterns] = useState(
    editing && isRuleDefinition(editing.definition)
      ? editing.definition.patterns.join("\n")
      : "",
  );
  const [instruction, setInstruction] = useState(
    editing && !isRuleDefinition(editing.definition) ? editing.definition.instruction : "",
  );
  const [action, setAction] = useState<string>(editing ? editing.definition.action : "block");
  const [saving, setSaving] = useState(false);

  // A rule redacts or blocks; a judge flags or blocks. Switching kind can strand
  // an action the other kind does not have, so normalise it here rather than
  // letting the server reject it.
  const actions = kind === "rule" ? ["block", "redact"] : ["block", "flag"];
  useEffect(() => {
    if (!actions.includes(action)) setAction("block");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const save = async () => {
    if (!key.trim()) {
      alert("A key is required.");
      return;
    }
    const definition: PolicyDefinition =
      kind === "rule"
        ? {
            patterns: patterns.split("\n").map((p) => p.trim()).filter(Boolean),
            action: action as "block" | "redact",
          }
        : { instruction: instruction.trim(), action: action as "block" | "flag" };

    if (kind === "rule" && (definition as { patterns: string[] }).patterns.length === 0) {
      alert("A rule needs at least one pattern.");
      return;
    }
    if (kind === "judge" && !instruction.trim()) {
      alert("A judge needs an instruction.");
      return;
    }

    setSaving(true);
    try {
      await policiesApi.upsert(scope, {
        key: key.trim(),
        phase,
        kind,
        definition,
        enabled: editing?.enabled ?? true,
      });
      onSaved();
    } catch (e: any) {
      alert(e.message || "Failed to save policy");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        border: "1px solid var(--border-color, #e0e0e0)",
        borderRadius: 8,
        padding: 16,
        marginBottom: 24,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        maxWidth: 640,
      }}
    >
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
        {editing ? `Edit ${editing.key}` : "New policy"}
      </h2>

      {editing ? (
        <div style={{ fontSize: 13 }}>
          <strong>Key:</strong> {editing.key}
        </div>
      ) : (
        <KeyCombobox value={key} onChange={setKey} knownKeys={knownKeys} />
      )}

      <KindSelector value={kind} onChange={setKind} />

      <div style={{ display: "flex", gap: 16 }}>
        <div>
          <label
            htmlFor="policy-phase"
            style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 6 }}
          >
            Phase
          </label>
          <select
            id="policy-phase"
            aria-label="Phase"
            value={phase}
            onChange={(e) => setPhase(e.target.value as PolicyPhase)}
            style={inputStyle}
          >
            <option value="pre">pre — the prompt</option>
            <option value="post">post — the reply</option>
            <option value="both">both</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="policy-action"
            style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 6 }}
          >
            Action
          </label>
          <select
            id="policy-action"
            aria-label="Action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            style={inputStyle}
          >
            {actions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      </div>

      {kind === "rule" ? (
        <div>
          <label
            htmlFor="policy-patterns"
            style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 6 }}
          >
            Patterns (one regex per line)
          </label>
          <textarea
            id="policy-patterns"
            aria-label="Patterns"
            value={patterns}
            onChange={(e) => setPatterns(e.target.value)}
            rows={4}
            style={{ ...inputStyle, width: "100%", fontFamily: "monospace" }}
          />
        </div>
      ) : (
        <div>
          <label
            htmlFor="policy-instruction"
            style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 6 }}
          >
            Instruction
          </label>
          <textarea
            id="policy-instruction"
            aria-label="Instruction"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            rows={3}
            style={{ ...inputStyle, width: "100%" }}
          />
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          style={{ fontSize: 13, padding: "6px 14px", cursor: "pointer" }}
        >
          {saving ? "Saving…" : "Save policy"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{ fontSize: 13, padding: "6px 14px", cursor: "pointer" }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview box
// ---------------------------------------------------------------------------

function PreviewBox({ scope }: { scope: PolicyScope }) {
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<PolicyPhase>("pre");
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const res = await policiesApi.preview({
        text,
        scope_tier: scope.tier,
        // The company tier's id lives in the caller's session server-side; for
        // preview there is nowhere else to put it, so send the tier name as a
        // self-describing placeholder the route resolves.
        scope_id: scope.scopeId ?? scope.tier,
        phase,
      });
      setResult(res);
    } catch (e: any) {
      alert(e.message || "Preview failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      style={{
        border: "1px solid var(--border-color, #e0e0e0)",
        borderRadius: 8,
        padding: 16,
        maxWidth: 640,
      }}
    >
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 4px" }}>Preview</h2>
      <p style={{ fontSize: 12, color: "var(--text-secondary, #666)", margin: "0 0 12px" }}>
        Runs this text through the same evaluator a turn uses, at this scope.
      </p>

      <textarea
        aria-label="Preview text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="Text to check…"
        style={{ ...inputStyle, width: "100%", marginBottom: 8 }}
      />

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <select
          aria-label="Preview phase"
          value={phase}
          onChange={(e) => setPhase(e.target.value as PolicyPhase)}
          style={inputStyle}
        >
          <option value="pre">pre</option>
          <option value="post">post</option>
          <option value="both">both</option>
        </select>
        <button
          type="button"
          onClick={run}
          disabled={running || !text.trim()}
          style={{ fontSize: 13, padding: "6px 14px", cursor: "pointer" }}
        >
          {running ? "Running…" : "Run preview"}
        </button>
      </div>

      {result && (
        <div style={{ fontSize: 13 }}>
          {/* stream_safe=false means the pattern silently never matches across a
              chunk boundary. Authoring time is the only moment anyone can act
              on that, so it goes first and loud — not into a warnings list. */}
          {!result.stream_safe && (
            <div
              style={{
                padding: 10,
                borderRadius: 6,
                border: "1px solid #d97706",
                background: "#fffbeb",
                color: "#92400e",
                marginBottom: 12,
              }}
            >
              <strong>Not stream-safe.</strong> A pattern here is wider than the streaming
              hold window, so it will silently never match across a chunk boundary on a
              reply. Shorten the pattern, or it will not do what you think.
            </div>
          )}

          <div style={{ marginBottom: 6 }}>
            Action: <strong>{result.action}</strong>
          </div>
          {result.action === "redact" && (
            <div style={{ marginBottom: 6 }}>
              Rewritten:{" "}
              <code style={{ background: "var(--surface-bg, #f5f5f5)", padding: "2px 4px" }}>
                {result.text}
              </code>
            </div>
          )}
          <div style={{ marginBottom: 6 }}>
            Matched:{" "}
            {result.matched.length === 0
              ? "nothing"
              : result.matched.map(([tier, key]) => `${tier}:${key}`).join(", ")}
          </div>
          {/* A pattern wider than the hold window silently never matches across
              a chunk boundary — the policy looks configured and catches
              nothing. Authoring time is the only moment this is actionable. */}
          {(result.stream_warnings ?? []).length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, color: "var(--danger-fg, #d33)" }}>
              {(result.stream_warnings ?? []).map((w) => (
                <li key={`${w.scope_tier}:${w.key}:${w.pattern}`}>
                  <strong>{w.scope_tier}:{w.key}</strong> — needs {w.needs_chars}{" "}
                  characters but the stream filter holds {w.hold_window_chars}.{" "}
                  {w.detail}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Effective view — the union across five levels for one tenant.
// ---------------------------------------------------------------------------

function groupByKey(rows: EffectivePolicyRow[]): [string, EffectivePolicyRow[]][] {
  const map = new Map<string, EffectivePolicyRow[]>();
  for (const r of rows) {
    const bucket = map.get(r.key);
    if (bucket) bucket.push(r);
    else map.set(r.key, [r]);
  }
  return [...map.entries()];
}

/** Two rows sharing a key is the union WORKING, never a collision. Say what
 *  each tier contributes rather than flagging a conflict. */
function unionSentence(rows: EffectivePolicyRow[]): string {
  if (rows.length < 2) return "";
  const [first, ...rest] = rows;
  const parts = rest.map((r) => `${TIER_LABEL[r.tier].toLowerCase()} additionally ${r.definition.action}s`);
  return `${TIER_LABEL[first.tier]} requires ${first.definition.action}; ${parts.join("; ")}.`;
}

function EffectiveView({ companyId, onClose }: { companyId: string; onClose: () => void }) {
  const [rows, setRows] = useState<EffectivePolicyRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    policiesApi
      .effective({ companyId })
      .then((res) => {
        if (live) setRows(res.items);
      })
      .catch((e: any) => alert(e.message || "Failed to load the effective view"))
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [companyId]);

  const grouped = useMemo(() => groupByKey(rows), [rows]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
          Effective policies — {companyId}
        </h2>
        <button type="button" onClick={onClose} style={{ fontSize: 12, padding: "4px 10px", cursor: "pointer" }}>
          Back to authoring
        </button>
      </div>
      <p style={{ fontSize: 12, color: "var(--text-secondary, #666)", marginBottom: 16 }}>
        What this tenant is subject to right now — the union of all five levels. Several
        tiers sharing a key is normal: each one applies.
      </p>

      {loading ? (
        <p>Loading…</p>
      ) : grouped.length === 0 ? (
        <p style={{ fontSize: 13 }}>No policies apply to this tenant.</p>
      ) : (
        grouped.map(([key, keyRows]) => (
          <div key={key} style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{key}</div>
            {keyRows.length > 1 && (
              <div style={{ fontSize: 12, color: "var(--text-secondary, #666)", marginBottom: 6 }}>
                {unionSentence(keyRows)}
              </div>
            )}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border-color, #e0e0e0)" }}>
                  <th style={th}>Tier</th>
                  <th style={th}>Phase</th>
                  <th style={th}>Kind</th>
                  <th style={th}>Action</th>
                  <th style={th}>Enabled</th>
                  <th style={th}>Origin</th>
                </tr>
              </thead>
              <tbody>
                {keyRows.map((r) => (
                  <tr
                    key={`${r.tier}:${r.scope_id}`}
                    style={{ borderBottom: "1px solid var(--border-color, #f0f0f0)" }}
                  >
                    <td style={td}>{TIER_LABEL[r.tier]}</td>
                    <td style={td}>{r.phase}</td>
                    <td style={td}>{r.kind}</td>
                    <td style={td}>{actionOf(r.definition)}</td>
                    <td style={td}>{r.enabled ? "on" : "parked"}</td>
                    <td style={td}>{r.inherited ? `inherited from ${TIER_LABEL[r.tier].toLowerCase()}` : "this scope"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

export function AdminPolicies() {
  const [tier, setTier] = useState<PolicyTier>("platform");
  const [scopeId, setScopeId] = useState("");

  // The industry vocabulary, from the table rather than a constant in this
  // file. A second copy is how "construction" ended up valid in the database
  // and refused by the application.
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [newIndustry, setNewIndustry] = useState<{
    code: string;
    display_name: string;
  } | null>(null);

  const loadIndustries = useCallback(async () => {
    try {
      setIndustries(await policiesApi.listIndustries());
    } catch {
      setIndustries([]);
    }
  }, []);

  useEffect(() => {
    loadIndustries();
  }, [loadIndustries]);
  const [rows, setRows] = useState<PolicyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<PolicyRow | null>(null);
  const [authoring, setAuthoring] = useState(false);
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [effectiveCompany, setEffectiveCompany] = useState<string | null>(null);
  /** Every key seen across the tiers visited this session. The combobox offers
   *  these so an author reuses a concern rather than inventing a near-miss. */
  const [knownKeys, setKnownKeys] = useState<string[]>([]);

  const scope: PolicyScope = useMemo(
    () => ({ tier, scopeId: scopeId || undefined }),
    [tier, scopeId],
  );

  const scopeReady = !NEEDS_SCOPE_ID.includes(tier) || Boolean(scopeId);

  const load = useCallback(async () => {
    if (!scopeReady) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await policiesApi.list(scope);
      setRows(res.items);
      setKnownKeys((prev) => [...new Set([...prev, ...res.items.map((r) => r.key)])].sort());
    } catch (e: any) {
      alert(e.message || "Failed to load policies");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [scope, scopeReady]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    adminApi
      .listOrgs({ page: 1, limit: 100 })
      .then((res) => setOrgs(res.items))
      .catch(() => {
        // The picker is a convenience; a failure here must not break authoring.
      });
  }, []);

  const toggleEnabled = async (row: PolicyRow) => {
    try {
      // Parking beats deleting: a delete loses `updated_by`.
      await policiesApi.setEnabled(scope, row.key, !row.enabled);
      load();
    } catch (e: any) {
      alert(e.message || "Failed to change the policy");
    }
  };

  const removeRow = async (row: PolicyRow) => {
    if (
      !confirm(
        `Delete "${row.key}" at this scope? This scope will re-inherit, and the row's ` +
          `author and history are lost. Disabling it instead keeps both.`,
      )
    )
      return;
    try {
      await policiesApi.remove(scope, row.key);
      load();
    } catch (e: any) {
      alert(e.message || "Failed to delete the policy");
    }
  };

  if (effectiveCompany) {
    return <EffectiveView companyId={effectiveCompany} onClose={() => setEffectiveCompany(null)} />;
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>Policies</h1>
      <p style={{ fontSize: 13, color: "var(--text-secondary, #666)", marginBottom: 20, maxWidth: 720 }}>
        The rules a turn is checked against. Five levels apply as a union — a tenant is
        subject to every level above it, and a narrower level cannot lift a broader one.
      </p>

      {/* Tier tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border-color, #e0e0e0)", marginBottom: 16 }}>
        {TIERS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tier === t}
            onClick={() => {
              setTier(t);
              setScopeId("");
              setEditing(null);
              setAuthoring(false);
            }}
            style={{
              padding: "8px 14px",
              fontSize: 13,
              cursor: "pointer",
              border: "none",
              background: "transparent",
              borderBottom: tier === t ? "2px solid var(--primary, #2563eb)" : "2px solid transparent",
              color: tier === t ? "var(--primary, #2563eb)" : "var(--text-primary, #333)",
              fontWeight: tier === t ? 600 : 400,
            }}
          >
            {TIER_LABEL[t]}
          </button>
        ))}
      </div>

      {/* Scope selectors */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
        {tier === "industry" && (
          <div>
            <label htmlFor="scope-industry" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              Industry
            </label>
            <select
              id="scope-industry"
              aria-label="Industry"
              value={scopeId}
              onChange={(e) => setScopeId(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select an industry…</option>
              {industries.map((i) => (
                <option key={i.code} value={i.code}>
                  {i.display_name}
                </option>
              ))}
            </select>
            {/* The vocabulary used to be editable only by migration — the
                endpoint that managed it was lost in the core_services
                consolidation, which is why "construction" was missing for
                months while contractors were the target market. */}
            <button
              type="button"
              onClick={() =>
                setNewIndustry(
                  newIndustry ? null : { code: "", display_name: "" },
                )
              }
              style={{
                background: "none",
                border: "none",
                padding: 0,
                marginTop: 6,
                fontSize: 12,
                color: "var(--accent, #3b82f6)",
                cursor: "pointer",
              }}
            >
              {newIndustry ? "Cancel" : "+ Add an industry"}
            </button>
            {newIndustry && (
              <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
                <input
                  aria-label="Industry name"
                  placeholder="Construction"
                  value={newIndustry.display_name}
                  onChange={(e) =>
                    setNewIndustry({
                      display_name: e.target.value,
                      // The code is the stable identifier a policy stores, so
                      // it is derived rather than typed — a display name can be
                      // corrected later, a code cannot without orphaning rules.
                      code: e.target.value
                        .trim()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "_")
                        .replace(/^_+|_+$/g, ""),
                    })
                  }
                  style={inputStyle}
                />
                <button
                  type="button"
                  disabled={!newIndustry.code}
                  onClick={async () => {
                    try {
                      await policiesApi.createIndustry({
                        code: newIndustry.code,
                        display_name: newIndustry.display_name.trim(),
                      });
                      setNewIndustry(null);
                      await loadIndustries();
                      setScopeId(newIndustry.code);
                    } catch (e) {
                      alert(e instanceof Error ? e.message : "Could not add it");
                    }
                  }}
                  style={{
                    padding: "4px 12px",
                    fontSize: 13,
                    cursor: newIndustry.code ? "pointer" : "not-allowed",
                  }}
                >
                  Add
                </button>
              </div>
            )}
            {newIndustry?.code && (
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
                Stored as <code>{newIndustry.code}</code>
              </div>
            )}
          </div>
        )}
        {(tier === "department" || tier === "user") && (
          <div>
            <label htmlFor="scope-id" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
              {tier === "department" ? "Department id" : "User id"}
            </label>
            <input
              id="scope-id"
              aria-label={tier === "department" ? "Department id" : "User id"}
              type="text"
              value={scopeId}
              onChange={(e) => setScopeId(e.target.value)}
              style={inputStyle}
            />
          </div>
        )}

        {/* Company picker → the effective view. "What is this tenant subject to
            right now" is the question a platform admin actually has, and the
            answer is the union, not one tier's list. */}
        <div style={{ marginLeft: "auto" }}>
          <label htmlFor="effective-company" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
            What is a tenant subject to?
          </label>
          <select
            id="effective-company"
            aria-label="Company effective view"
            value=""
            onChange={(e) => e.target.value && setEffectiveCompany(e.target.value)}
            style={inputStyle}
          >
            <option value="">Select a company…</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!scopeReady ? (
        <p style={{ fontSize: 13, color: "var(--text-secondary, #666)" }}>
          Choose a {tier === "industry" ? "industry" : tier} to see its policies.
        </p>
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setAuthoring(true);
              }}
              style={{ fontSize: 13, padding: "6px 14px", cursor: "pointer" }}
            >
              New policy
            </button>
          </div>

          {(authoring || editing) && (
            <PolicyEditor
              scope={scope}
              knownKeys={knownKeys}
              editing={editing}
              onSaved={() => {
                setAuthoring(false);
                setEditing(null);
                load();
              }}
              onCancel={() => {
                setAuthoring(false);
                setEditing(null);
              }}
            />
          )}

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 24 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border-color, #e0e0e0)" }}>
                <th style={th}>Key</th>
                <th style={th}>Phase</th>
                <th style={th}>Kind</th>
                <th style={th}>Action</th>
                <th style={th}>Enabled</th>
                <th style={th}>Updated by</th>
                <th style={th}>Updated at</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && !rows.length ? (
                <tr>
                  <td colSpan={8} style={{ padding: 16, textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 16, textAlign: "center" }}>
                    No policies at this scope
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.key} style={{ borderBottom: "1px solid var(--border-color, #f0f0f0)" }}>
                    <td style={{ ...td, fontWeight: 500 }}>{r.key}</td>
                    <td style={td}>{r.phase}</td>
                    <td style={td}>{r.kind}</td>
                    <td style={td}>{actionOf(r.definition)}</td>
                    <td style={td}>
                      <button
                        type="button"
                        onClick={() => toggleEnabled(r)}
                        aria-label={r.enabled ? `Disable ${r.key}` : `Enable ${r.key}`}
                        style={{
                          fontSize: 11,
                          padding: "2px 8px",
                          cursor: "pointer",
                          borderRadius: 4,
                          border: "1px solid var(--border-color, #e0e0e0)",
                          background: r.enabled ? "#ecfdf5" : "transparent",
                          color: r.enabled ? "#047857" : "var(--text-secondary, #666)",
                        }}
                      >
                        {r.enabled ? "Enabled" : "Disabled"}
                      </button>
                    </td>
                    <td style={td}>{r.updated_by ?? "—"}</td>
                    <td style={td}>{fmtDate(r.updated_at)}</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <button
                        type="button"
                        onClick={() => {
                          setAuthoring(false);
                          setEditing(r);
                        }}
                        style={{ fontSize: 11, padding: "2px 6px", cursor: "pointer", marginRight: 4 }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => removeRow(r)}
                        style={{ fontSize: 11, padding: "2px 6px", cursor: "pointer", color: "red" }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <PreviewBox scope={scope} />
        </>
      )}
    </div>
  );
}
